/**
 * Equipment Matcher Utility
 *
 * Matches suggested equipment IDs based on campaign frame mode:
 * - Core mode: Direct ID matching
 * - Beast Feast mode: Match by trait, damage type (weapons), and armor weight
 */

/**
 * Match a primary weapon based on criteria
 * @param {Object} criteria - { trait, damageType } or direct weaponId
 * @param {Object} availableWeapons - The available weapons object/array
 * @param {string} campaignFrame - 'core' or campaign frame name
 * @returns {string|null} - Weapon ID or null if no match
 */
export function matchPrimaryWeapon(criteria, availableWeapons, campaignFrame) {
  if (!criteria) return null;

  // Core mode: direct ID match
  if (campaignFrame === 'core' && typeof criteria === 'string') {
    return criteria;
  }

  // Campaign frame mode: match by trait and damage type
  if (typeof criteria === 'object' && criteria.trait && criteria.damageType) {
    // Convert to array if it's an object
    const weaponsArray = Array.isArray(availableWeapons)
      ? availableWeapons
      : Object.entries(availableWeapons).map(([id, weapon]) => ({ ...weapon, id }));

    console.log('[equipmentMatcher] Primary weapon search:', {
      criteria,
      campaignFrame,
      weaponsCount: weaponsArray.length,
      sampleWeapons: weaponsArray.slice(0, 3)
    });

    // Find first weapon matching trait and damage type
    const match = weaponsArray.find((weapon) => {
      const matches = weapon.trait === criteria.trait &&
        weapon.damage && weapon.damage.includes(criteria.damageType);

      if (matches) {
        console.log('[equipmentMatcher] Primary weapon MATCH found:', weapon);
      }

      return matches;
    });

    console.log('[equipmentMatcher] Primary weapon result:', match ? match.id : 'NO MATCH');
    return match ? match.id : null;
  }

  return null;
}

/**
 * Match a secondary weapon based on criteria
 * @param {Object} criteria - { trait, damageType } or direct weaponId
 * @param {Array|Object} availableWeapons - The available secondary weapons (array for campaign frames, object for core)
 * @param {string} campaignFrame - 'core' or campaign frame name
 * @returns {string|null} - Weapon ID or null if no match
 */
export function matchSecondaryWeapon(criteria, availableWeapons, campaignFrame) {
  if (!criteria) return null;

  // Core mode: direct ID match
  if (campaignFrame === 'core' && typeof criteria === 'string') {
    return criteria;
  }

  // Campaign frame mode: match by trait and damage type
  if (typeof criteria === 'object' && criteria.trait && criteria.damageType) {
    // Convert to array if it's an object
    const weaponsArray = Array.isArray(availableWeapons)
      ? availableWeapons
      : Object.entries(availableWeapons).map(([id, weapon]) => ({ ...weapon, id }));

    // Find first weapon matching trait and damage type
    const match = weaponsArray.find((weapon) => {
      return (
        weapon.trait === criteria.trait &&
        weapon.damage && weapon.damage.includes(criteria.damageType)
      );
    });

    return match ? match.id : null;
  }

  return null;
}

/**
 * Match armor based on criteria
 * @param {Object} criteria - { type: 'light'|'heavy' } or direct armorId
 * @param {Array|Object} availableArmor - The available armor (array for campaign frames, object for core)
 * @param {string} campaignFrame - 'core' or campaign frame name
 * @returns {string|null} - Armor ID or null if no match
 */
export function matchArmor(criteria, availableArmor, campaignFrame) {
  if (!criteria) return null;

  // Core mode: direct ID match
  if (campaignFrame === 'core' && typeof criteria === 'string') {
    return criteria;
  }

  // Campaign frame mode: match by armor type (light/heavy)
  if (typeof criteria === 'object' && criteria.type) {
    // Convert to array if it's an object
    const armorArray = Array.isArray(availableArmor)
      ? availableArmor
      : Object.entries(availableArmor).map(([id, armor]) => ({ ...armor, id }));

    // Determine if armor is light or heavy based on feature or name
    // Light armor: Has "Flexible" feature, or names like Gambeson, Leather, Quilted
    // Heavy armor: Has "Heavy" or "Very Heavy" feature, or names like Chain, Plate, Bark
    const isLightArmor = (armor) => {
      const feature = armor.feature?.toLowerCase() || '';
      const name = armor.name?.toLowerCase() || '';

      // Check feature first (more reliable)
      if (feature.includes('flexible')) return true;
      if (feature.includes('heavy')) return false;

      // Fallback to name matching
      return name.includes('gambeson') ||
             name.includes('leather') ||
             name.includes('quilted') ||
             name.includes('apron');
    };

    const match = armorArray.find((armor) => {
      if (criteria.type === 'light') {
        return isLightArmor(armor);
      } else if (criteria.type === 'heavy') {
        return !isLightArmor(armor);
      }

      return false;
    });

    return match ? match.id : null;
  }

  return null;
}

/**
 * Match all suggested equipment for a class
 * @param {Object} suggestedEquipment - Class guidance equipment object
 * @param {Array|Object} availablePrimaryWeapons - Available primary weapons
 * @param {Array|Object} availableSecondaryWeapons - Available secondary weapons
 * @param {Array|Object} availableArmor - Available armor
 * @param {string} campaignFrame - 'core' or campaign frame name
 * @returns {Object} - { weaponChoice, primaryWeapon, secondaryWeapon, armor }
 */
export function matchSuggestedEquipment(
  suggestedEquipment,
  availablePrimaryWeapons,
  availableSecondaryWeapons,
  availableArmor,
  campaignFrame
) {
  console.log('[equipmentMatcher] matchSuggestedEquipment called:', {
    campaignFrame,
    hasSuggestedEquipment: !!suggestedEquipment,
    primaryWeaponsCount: Array.isArray(availablePrimaryWeapons) ? availablePrimaryWeapons.length : Object.keys(availablePrimaryWeapons || {}).length,
    secondaryWeaponsCount: Array.isArray(availableSecondaryWeapons) ? availableSecondaryWeapons.length : Object.keys(availableSecondaryWeapons || {}).length
  });

  if (!suggestedEquipment) return null;

  // Try frame-specific config (e.g., 'beast-feast' → 'beastFeast'), fall back to core
  const frameCamelCase = campaignFrame.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
  const frameSpecificConfig = suggestedEquipment[frameCamelCase];
  const usingCoreConfig = campaignFrame === 'core' || !frameSpecificConfig;
  const config = usingCoreConfig ? suggestedEquipment.core : frameSpecificConfig;

  // When using core config (even for non-core frames), matchers need 'core' mode for direct ID matching
  const matcherFrame = usingCoreConfig ? 'core' : campaignFrame;

  console.log('[equipmentMatcher] Config selected:', {
    configExists: !!config,
    usingCoreConfig,
    matcherFrame,
    weaponChoice: config?.weaponChoice,
    primaryWeaponCriteria: config?.primaryWeapon,
    secondaryWeaponCriteria: config?.secondaryWeapon,
    armorCriteria: config?.armor
  });

  if (!config) return null;

  // Match primary weapon first
  const primaryWeaponId = matchPrimaryWeapon(
    config.primaryWeapon,
    availablePrimaryWeapons,
    matcherFrame
  );

  // Find the matched primary weapon to check its burden
  const weaponsArray = Array.isArray(availablePrimaryWeapons)
    ? availablePrimaryWeapons
    : Object.entries(availablePrimaryWeapons).map(([id, weapon]) => ({ ...weapon, id }));

  const matchedPrimaryWeapon = weaponsArray.find(w => w.id === primaryWeaponId);

  // Auto-detect weapon choice based on matched weapon's burden
  let weaponChoice = config.weaponChoice || 'two-handed';
  let secondaryWeapon = '';

  if (matchedPrimaryWeapon) {
    const burden = matchedPrimaryWeapon.burden?.toLowerCase() || '';

    if (burden.includes('one-handed')) {
      // If matched weapon is one-handed, force dual-wield and match secondary
      weaponChoice = 'dual-wield';

      // Try to match secondary weapon using config criteria if available
      let secondaryCriteria = config.secondaryWeapon;

      // If no secondary criteria (e.g., class was configured for two-handed),
      // create intelligent fallback criteria
      if (!secondaryCriteria && typeof config.primaryWeapon === 'object') {
        // Fallback strategy: Look for a shield first, or use same trait as primary
        console.log('[equipmentMatcher] No secondary criteria - using intelligent fallback');

        // First try to find a shield (Protective or Barrier feature)
        const secondaryArray = Array.isArray(availableSecondaryWeapons)
          ? availableSecondaryWeapons
          : Object.entries(availableSecondaryWeapons).map(([id, weapon]) => ({ ...weapon, id }));

        const shield = secondaryArray.find(w =>
          w.feature?.toLowerCase().includes('protective') ||
          w.feature?.toLowerCase().includes('barrier')
        );

        if (shield) {
          secondaryWeapon = shield.id;
          console.log('[equipmentMatcher] Found shield as secondary:', shield);
        } else {
          // If no shield, use same trait/damage as primary
          secondaryCriteria = config.primaryWeapon;
          secondaryWeapon = matchSecondaryWeapon(
            secondaryCriteria,
            availableSecondaryWeapons,
            matcherFrame
          ) || '';
          console.log('[equipmentMatcher] Using same criteria as primary for secondary');
        }
      } else {
        // Use configured secondary criteria
        secondaryWeapon = matchSecondaryWeapon(
          secondaryCriteria,
          availableSecondaryWeapons,
          matcherFrame
        ) || '';
      }

      console.log('[equipmentMatcher] Primary weapon is one-handed, auto-switching to dual-wield');
    } else if (burden.includes('two-handed')) {
      // If matched weapon is two-handed, force two-handed choice
      weaponChoice = 'two-handed';
      secondaryWeapon = '';

      console.log('[equipmentMatcher] Primary weapon is two-handed');
    }
  }

  const result = {
    weaponChoice,
    primaryWeapon: primaryWeaponId,
    secondaryWeapon,
    armor: matchArmor(config.armor, availableArmor, matcherFrame)
  };

  console.log('[equipmentMatcher] Final matched equipment:', result);

  return result;
}
